/**
 * Change password, for email + password accounts (api/src/auth/password.ts). `forced` is the screen shown
 * instead of the site while the account still has the one-time password an admin passed on.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiPut, errorText } from "../lib/api.ts";
import { signOut } from "../lib/auth.ts";
import { LangSwitch, t } from "../lib/i18n.tsx";
import { useMe } from "../hooks/useMe.tsx";

// Server messages (routes/password_accounts.ts) shown in the site's language.
const SERVER_MESSAGES = [
  "Current password is wrong",
  "At least 10 characters",
  "Can't be your email",
  "Choose a password different from the current one",
  "This account signs in with Microsoft",
];

export function PasswordPage({ forced = false }: { forced?: boolean }) {
  const me = useMe();
  const qc = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  if (me.data && me.data.via !== "password" && me.data.via !== "dev") {
    return <div className="u-page"><p>{t("This account signs in with Microsoft. Change its password with Microsoft.")}</p></div>;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== again) return setMsg({ ok: false, text: t("The new passwords don't match.") });
    setSaving(true);
    setMsg(null);
    try {
      await apiPut("/api/me/password", { current, next });
      setCurrent("");
      setNext("");
      setAgain("");
      setMsg({ ok: true, text: t("Password changed.") });
      // The site opens once /api/me no longer says the password must change.
      await qc.invalidateQueries();
    } catch (err) {
      const text = errorText(err);
      setMsg({ ok: false, text: SERVER_MESSAGES.includes(text) ? t(text) : text });
    } finally {
      setSaving(false);
    }
  };

  const form = (
    <form className="pw-form" onSubmit={submit}>
      <input type="text" name="username" autoComplete="username" value={me.data?.email ?? ""} readOnly hidden />
      <label>
        {forced ? t("One-time password") : t("Current password")}
        <input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <label>
        {t("New password")} <span className="muted">{t("(at least 10 characters)")}</span>
        <input type="password" autoComplete="new-password" required minLength={10} value={next} onChange={(e) => setNext(e.target.value)} />
      </label>
      <label>
        {t("New password again")}
        <input type="password" autoComplete="new-password" required minLength={10} value={again} onChange={(e) => setAgain(e.target.value)} />
      </label>
      {msg && <div className={msg.ok ? "pw-ok" : "u-error"}>{msg.text}</div>}
      <button type="submit" className="btn primary" disabled={saving}>{saving ? t("Saving…") : t("Change password")}</button>
    </form>
  );

  if (!forced) {
    return (
      <div className="u-page">
        <p className="muted">{t("For signing in with email and password.")}</p>
        {form}
        <p><Link to="/">{t("Back to Home")}</Link></p>
      </div>
    );
  }
  return (
    <div className="pw-forced">
      <div className="pw-card">
        <div className="pw-top">
          <h2>{t("Choose your password")}</h2>
          <LangSwitch />
        </div>
        <p className="muted">{t("You signed in with a one-time password. Choose your own to open the site.")}</p>
        {form}
        <button type="button" className="btn sm" onClick={async () => { await signOut(); location.assign("/signed-out"); }}>
          {t("Sign out")}
        </button>
      </div>
    </div>
  );
}
